import { Fragment } from "react";
import { useStore } from "react-redux";
import { FlightSearchSelectors } from "@b2bportal/core-flights";
import { isWebView } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { RootState } from "../../../store";
import { saveStateForLanguageChange } from "../../../store/languageChangePersistence";
import styles from "./styles.module.scss";
import { getLanguageChangeRedirectPath } from "./utils";

export const LanguageToggle = () => {
  const { language, setLanguage, brand } = useI18nContext();
  const store = useStore<RootState>();

  const handleLanguageChange = (langKey: string) => {
    if (language !== langKey) {
      // Save Redux state to sessionStorage before the reload
      const currentState = store.getState();
      saveStateForLanguageChange(currentState);

      const redirectPath = getLanguageChangeRedirectPath(
        window.location.pathname,
        FlightSearchSelectors.getTripCategory(currentState)
      );
      if (redirectPath != null) {
        // Keep the search query params so the user lands back on the same search
        const url = new URL(window.location.href);
        url.pathname = redirectPath;
        window.history.replaceState({}, "", url);
      }

      // This will trigger a page reload (in core library)
      setLanguage(langKey);
    }
  };

  // Hide language toggle in webviews - the native app controls language
  if (!brand?.supportedLanguages || isWebView()) {
    return null;
  }

  return (
    <div className={styles.LanguageToggle}>
      {brand.supportedLanguages.map((lang, index) => (
        <Fragment key={lang.key}>
          {index > 0 && <span className={styles.separator}>|</span>}
          <button
            type="button"
            onClick={() => handleLanguageChange(lang.key)}
            className={language === lang.key ? styles.active : styles.inactive}
            aria-label={`Switch to ${lang.name}`}
            aria-current={language === lang.key || undefined}
            aria-pressed={language === lang.key}
          >
            {lang.name}
          </button>
        </Fragment>
      ))}
    </div>
  );
};
